import type { Paginated } from '../types/pagination'
import type { Attachment } from '../types/project'
import type { Tag, TagStatus } from '../types/tag'
import type { MultiTagReport } from '../types/workorder'
import { api } from './fetch'
import { loadService } from './loadService'

class OfflineError extends Error {
  constructor() {
    super('Offline - will sync when online')
    this.name = 'OfflineError'
  }
}

export interface TagFilters {
  search?: string
  created_after?: string
  created_before?: string
  start_after?: string
  start_before?: string
  assigned_user_id?: number
  status?: string
  exclude_status?: string
}

export const tagService = {
  async generateLabel(): Promise<{
    label: string
    creation_date_formatted: string
  }> {
    try {
      const response = await api.get<{
        label: string
        creation_date_formatted: string
      }>('/tag/generate-label/')
      return response.data
    } catch (error) {
      console.error('Failed to generate tag label:', error)
      throw error
    }
  },

  async getTags(
    filters?: TagFilters & { page?: number; page_size?: number },
  ): Promise<Paginated<Tag>> {
    try {
      const params = new URLSearchParams()

      if (filters?.search) {
        params.append('search', filters.search)
      }
      if (filters?.created_after) {
        params.append('created_after', filters.created_after)
      }
      if (filters?.created_before) {
        params.append('created_before', filters.created_before)
      }
      if (filters?.start_after) {
        params.append('start_after', filters.start_after)
      }
      if (filters?.start_before) {
        params.append('start_before', filters.start_before)
      }
      if (filters?.assigned_user_id) {
        params.append('assigned_user_id', filters.assigned_user_id.toString())
      }
      if (filters?.status) {
        params.append('status', filters.status)
      }
      if (filters?.exclude_status) {
        params.append('exclude_status', filters.exclude_status)
      }
      if (filters?.page) {
        params.append('page', String(filters.page))
      }
      if (filters?.page_size) {
        params.append('page_size', String(filters.page_size))
      }

      const queryString = params.toString()
      const url = queryString ? `/tag/?${queryString}` : '/tag/'

      const response = await api.get<Paginated<Tag> | Tag[]>(url)
      const data = response.data as unknown
      if (Array.isArray(data)) {
        // Older backends return a plain array and ignore the status,
        // exclude_status, and page params — apply them client-side so the
        // dashboards behave the same regardless of deploy order
        let items = data as Tag[]
        if (filters?.status) {
          const wanted = filters.status
            .split(',')
            .map((s) => s.trim().toUpperCase())
            .filter(Boolean)
          items = items.filter((tag) =>
            wanted.includes(tag.status.toUpperCase()),
          )
        }
        if (filters?.exclude_status) {
          const excluded = filters.exclude_status
            .split(',')
            .map((s) => s.trim().toUpperCase())
            .filter(Boolean)
          items = items.filter(
            (tag) => !excluded.includes(tag.status.toUpperCase()),
          )
        }
        const total = items.length
        const size = Math.max(1, filters?.page_size ?? (total || 1))
        const current = Math.max(1, filters?.page ?? 1)
        const start = (current - 1) * size
        return {
          items: items.slice(start, start + size),
          count: total,
          page: current,
          pages: Math.max(1, Math.ceil(total / size)),
        }
      }
      return data as Paginated<Tag>
    } catch (error) {
      console.error('Failed to fetch tags:', error)
      throw error
    }
  },

  async getCreatedTags(params?: { page?: number; page_size?: number }): Promise<
    Paginated<Tag>
  > {
    try {
      const usp = new URLSearchParams()
      if (params?.page) usp.append('page', String(params.page))
      if (params?.page_size) usp.append('page_size', String(params.page_size))
      const qs = usp.toString()
      const response = await api.get<Paginated<Tag>>(
        `/tag/created/${qs ? `?${qs}` : ''}`,
      )
      return response.data
    } catch (error) {
      console.error('Failed to fetch created tags:', error)
      throw error
    }
  },

  async updateTagStatus(tagId: string, status: TagStatus): Promise<Tag> {
    try {
      const response = await api.patch(`/tag/${tagId}/driver/${status}/`)
      return response.data
    } catch (error) {
      console.error('Failed to update tag status:', error)
      throw error
    }
  },

  async updateTagSignature(
    tagId: string,
    signatureData: {
      supervisor_signature_data?: string
      driver_signature_data?: string
    },
  ): Promise<Tag> {
    try {
      const response = await api.patch(
        `/tag/${tagId}/signatures/upload/`,
        signatureData,
      )
      return response.data
    } catch (error) {
      if (!navigator.onLine) {
        console.log('Network error, queueing signature upload')
        throw new OfflineError()
      }
      console.error('Failed to update signature:', error)
      throw error
    }
  },

  async patchTag(tagId: string, updates: Partial<Tag>): Promise<Tag> {
    try {
      const {
        status,
        supervisor_signature,
        driver_signature,
        assigned_to,
        assigned_to_id,
        workorder,
        workorder_id,
        ...otherUpdates
      } = updates as Partial<Tag> & {
        assigned_to_id?: number
        workorder_id?: number
      }

      const response = await api.patch(`/tag/${tagId}/`, otherUpdates)
      return response.data
    } catch (error) {
      console.error('Failed to update tag:', error)
      throw error
    }
  },

  subscribeToUpdates(callback: (tags: Tag[]) => void): void {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener(
        'message',
        (event: MessageEvent<ServiceWorkerMessage>) => {
          if (
            event.data?.type === 'SYNC_COMPLETE' &&
            Array.isArray(event.data.tags)
          ) {
            callback(event.data.tags)
          }
        },
      )
    }
  },

  async createTag(tagData: {
    assigned_user_id: number
    created_by_id: number
    workorder_id?: number
    project_id?: number
    start_date: string
    destination_address: string
    entity: string
    origin_address: string
    origin_name: string
    title: string
    customer: string
    material: string
    load_info?: Record<string, unknown>
    notes?: string
    additional_expenses?: Record<string, unknown>
  }): Promise<Tag> {
    try {
      const response = await api.post<Tag>('/tag/', {
        ...tagData,
        created_date: new Date().toISOString(),
        updated_date: new Date().toISOString(),
        load_info: tagData.load_info || {},
        additional_expenses: {
          tolls: '0',
          yardTime: '0',
          homeTime: '0',
          notes: '',
          ...tagData.additional_expenses,
        },
      })
      return response.data
    } catch (error) {
      console.error('Failed to create tag:', error)
      throw error
    }
  },

  getTag: async (tagId: string): Promise<Tag> => {
    try {
      const response = await api.get<Tag>(`/tag/${tagId}/`)
      return response.data
    } catch (error) {
      console.error('Failed to fetch tag:', error)
      throw error
    }
  },

  getCompletedTags: async (): Promise<Tag[]> => {
    try {
      // Status report generation needs every completed tag, so follow the
      // pagination through all pages if the backend ever paginates by default
      let res = await tagService.getTags({ status: 'COMPLETE' })
      const completedTags = [...res.items]
      while (res.page < res.pages) {
        res = await tagService.getTags({
          status: 'COMPLETE',
          page: res.page + 1,
        })
        completedTags.push(...res.items)
      }
      return completedTags.sort(
        (a, b) =>
          new Date(b.updated_date).getTime() -
          new Date(a.updated_date).getTime(),
      )
    } catch (error) {
      console.error('Failed to fetch completed tags:', error)
      throw error
    }
  },

  getLoadCount: async (tagId: string): Promise<number> => {
    try {
      const response = await api.get<{ count: number }>(
        `/tag/${tagId}/load-count`,
      )
      return response.data.count
    } catch (error) {
      console.error('Failed to fetch load count:', error)
      throw error
    }
  },

  async calculateTotalTime(tagId: string, tag?: Tag): Promise<number> {
    try {
      // Get all loads for the tag
      const loads = await loadService.getLoadsByTag(tagId)
      if (!loads.length) {
        return 0
      }

      // Calculate load time (from first load arrival to last load departure)
      const firstLoad = loads.find((load) => load.number === 1)
      const lastLoad = loads.reduce(
        (max, load) => (load.number > (max?.number || 0) ? load : max),
        loads[0],
      )

      const startTime = firstLoad?.arrival_time
        ? new Date(firstLoad.arrival_time).getTime()
        : 0
      const endTime = lastLoad?.unload_departure_time
        ? new Date(lastLoad.unload_departure_time).getTime()
        : 0

      if (!startTime || !endTime) {
        console.log('Missing start or end time')
        return 0
      }

      let loadTime = endTime - startTime
      // If end time is less than start time, it's next day
      if (loadTime < 0) {
        loadTime += 24 * 60 * 60 * 1000 // Add 24 hours in milliseconds
      }

      // Calculate yard time and home time from additional expenses
      const yardTimeMinutes = Number.parseInt(
        tag?.additional_expenses?.yardTime || '0',
      )
      const homeTimeMinutes = Number.parseInt(
        tag?.additional_expenses?.homeTime || '0',
      )
      const yardTimeMs = yardTimeMinutes * 60 * 1000
      const homeTimeMs = homeTimeMinutes * 60 * 1000

      // Calculate total time in milliseconds
      const totalTimeMs = loadTime + yardTimeMs + homeTimeMs

      // Convert to hours and round to 2 decimal places
      const totalHours =
        Math.round((totalTimeMs / (1000 * 60 * 60)) * 100) / 100

      return totalHours
    } catch (error) {
      console.error('Failed to calculate total time:', error)
      return 0
    }
  },

  async generateReport(tagIds: string[]): Promise<MultiTagReport> {
    try {
      const response = await api.post<MultiTagReport>('/tag/report/generate/', {
        tag_ids: tagIds,
      })
      return response.data
    } catch (error) {
      console.error('Failed to generate report:', error)
      throw error
    }
  },

  /**
   * Save a new tag as a draft
   */
  async saveDraft(tagData: {
    assigned_user_id?: number
    created_by_id: number
    workorder_id?: number
    project_id?: number
    start_date?: string
    destination_address?: string
    entity: string
    origin_address?: string
    origin_name?: string
    title?: string
    customer?: string
    material?: string
    load_info?: Record<string, unknown>
    notes?: string
    additional_expenses?: Record<string, unknown>
    pay_rate?: string | number
    label?: string
  }): Promise<Tag> {
    try {
      // Only send allowed fields - don't spread tagData to avoid sending extra fields
      const response = await api.post<Tag>('/tag/', {
        status: 'DRAFT',
        created_date: new Date().toISOString(),
        updated_date: new Date().toISOString(),
        created_by_id: tagData.created_by_id,
        entity: tagData.entity,
        label: tagData.label,
        workorder_id: tagData.workorder_id,
        project_id: tagData.project_id,
        assigned_user_id: tagData.assigned_user_id,
        start_date: tagData.start_date,
        destination_address: tagData.destination_address,
        origin_address: tagData.origin_address,
        origin_name: tagData.origin_name,
        title: tagData.title,
        customer: tagData.customer,
        material: tagData.material,
        notes: tagData.notes,
        pay_rate: tagData.pay_rate,
        load_info: tagData.load_info || {},
        additional_expenses: {
          tolls: '0',
          yardTime: '0',
          homeTime: '0',
          notes: '',
          ...tagData.additional_expenses,
        },
      })
      return response.data
    } catch (error) {
      console.error('Failed to save tag draft:', error)
      throw error
    }
  },

  /**
   * Update an existing draft tag
   */
  async updateDraft(
    tagId: string,
    tagData: Partial<Tag> & { assigned_user_id?: number },
  ): Promise<Tag> {
    try {
      // Only send fields that are editable by tag sender (from TAG_SENDER_EDITABLE_FIELDS)
      const editableFields: Record<string, unknown> = {}

      // Editable fields for tag sender
      if (tagData.start_date !== undefined)
        editableFields.start_date = tagData.start_date
      if (tagData.destination_address !== undefined)
        editableFields.destination_address = tagData.destination_address
      if (tagData.origin_address !== undefined)
        editableFields.origin_address = tagData.origin_address
      if (tagData.origin_name !== undefined)
        editableFields.origin_name = tagData.origin_name
      if (tagData.material !== undefined)
        editableFields.material = tagData.material
      if (tagData.title !== undefined) editableFields.title = tagData.title
      if (tagData.notes !== undefined) editableFields.notes = tagData.notes
      if (tagData.customer !== undefined)
        editableFields.customer = tagData.customer
      if (tagData.pay_rate !== undefined)
        editableFields.pay_rate = tagData.pay_rate
      if (tagData.assigned_user_id !== undefined)
        editableFields.assigned_user_id = tagData.assigned_user_id

      const response = await api.patch<Tag>(`/tag/${tagId}/`, editableFields)
      return response.data
    } catch (error) {
      console.error('Failed to update tag draft:', error)
      throw error
    }
  },

  /**
   * Send a draft tag (transition from DRAFT to NEW)
   */
  async sendDraft(tagId: string): Promise<Tag> {
    try {
      const response = await api.post<Tag>(`/tag/${tagId}/send/`)
      return response.data
    } catch (error) {
      console.error('Failed to send tag draft:', error)
      throw error
    }
  },

  /**
   * Delete a draft tag
   */
  async deleteDraft(tagId: string): Promise<void> {
    try {
      await api.delete(`/tag/${tagId}`)
    } catch (error) {
      console.error('Failed to delete tag draft:', error)
      throw error
    }
  },

  /**
   * Upload attachments to a tag.
   */
  async uploadAttachments(tagId: string, files: File[]): Promise<Attachment[]> {
    try {
      const formData = new FormData()
      for (const file of files) {
        formData.append('files', file)
      }

      const response = await api.post<{
        success: boolean
        message: string
        attachments: Attachment[]
      }>(`/tag/${tagId}/attachments/upload/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      return response.data.attachments
    } catch (error) {
      console.error('Failed to upload tag attachments:', error)
      throw error
    }
  },

  /**
   * Get all attachments for a tag.
   */
  async getAttachments(tagId: string): Promise<Attachment[]> {
    try {
      const response = await api.get<Attachment[]>(`/tag/${tagId}/attachments/`)
      return response.data
    } catch (error) {
      console.error('Failed to get tag attachments:', error)
      throw error
    }
  },

  /**
   * Delete an attachment from a tag.
   */
  async deleteAttachment(tagId: string, attachmentId: number): Promise<void> {
    try {
      await api.delete(`/tag/${tagId}/attachments/${attachmentId}/`)
    } catch (error) {
      console.error('Failed to delete tag attachment:', error)
      throw error
    }
  },
}

export const getAdditionalExpenses = async (tagId: string) => {
  try {
    const response = await api.get<Tag>(`/tag/${tagId}/`)
    // Safely access nested properties with optional chaining
    const additionalExpenses = response.data?.additional_expenses || {}
    return {
      tolls: additionalExpenses.tolls || '',
      yardTime: additionalExpenses.yardTime || '',
      homeTime: additionalExpenses.homeTime || '',
      notes: response.data?.notes || '',
    }
  } catch (error) {
    console.error('Failed to fetch additional expenses:', error)
    // Return default values if the request fails
    return {
      tolls: '',
      yardTime: '',
      homeTime: '',
      notes: '',
    }
  }
}

export const getDriverDetails = async (userId: string) => {
  try {
    const response = await api.get(`/user/${userId}`)
    return {
      username: response.data.username,
      phoneNumber: response.data.phone_number,
      stakeholderType: response.data.stakeholder_type,
      name: `${response.data.first_name} ${response.data.last_name}`.trim(),
      email: response.data.email,
    }
  } catch (error) {
    console.error('Failed to fetch driver details:', error)
    throw error
  }
}

export const updateAdditionalExpenses = async (
  tagId: string,
  expenses: {
    tolls: string
    yardTime?: string
    homeTime?: string
    notes: string
  },
) => {
  try {
    const response = await api.patch(
      `/tag/${tagId}/driver/ADDITIONAL_EXPENSES/`,
      {
        additional_expenses: {
          tolls: expenses.tolls,
          yardTime: expenses.yardTime || '',
          homeTime: expenses.homeTime || '',
        },
        notes: expenses.notes,
      },
    )
    return response.data
  } catch (error) {
    console.error('Failed to update additional expenses:', error)
    throw error
  }
}

export const updateSupervisorInfo = async (
  tagId: string,
  info: {
    supervisor_name?: string
    supervisor_phone?: string
    supervisor_email?: string
  },
) => {
  try {
    const response = await api.patch(
      `/tag/${tagId}/driver/UPDATE_SUPERVISOR/`,
      info,
    )
    return response.data
  } catch (error) {
    console.error('Failed to update supervisor info:', error)
    throw error
  }
}

export const updateSharedEmails = async (tagId: string, emails: string[]) => {
  try {
    const response = await api.patch(`/tag/${tagId}/driver/SHARED_EMAILS/`, {
      shared_emails: emails,
    })
    return response.data
  } catch (error) {
    console.error('Failed to update shared emails:', error)
    throw error
  }
}

/**
 * Returns the contractor name from the project's created_by object.
 * If there is no project or created_by object, returns "-".
 */
export async function getRelatedContractorName(tag: Tag): Promise<string> {
  try {
    // Get project ID from either workorder.project or project.id
    const workorder = tag.workorder as unknown as {
      project: number | { id: number }
    } | null
    let projectId: number | null = null

    if (workorder?.project) {
      projectId =
        typeof workorder.project === 'object'
          ? workorder.project.id
          : workorder.project
    } else if (tag.project) {
      projectId =
        typeof tag.project === 'object'
          ? (tag.project as { id: number }).id
          : tag.project
    }

    if (projectId) {
      const projectResponse = await api.get(`/project/${projectId}`)
      const project = projectResponse.data
      if (project.created_by) {
        // project.created_by is now a full User object, no need for additional API call
        return (
          `${project.created_by.first_name || ''} ${project.created_by.last_name || ''}`.trim() ||
          '-'
        )
      }
    }
  } catch (error) {
    console.error('Failed to fetch project or contractor:', error)
  }
  return '-'
}

/**
 * Returns the broker name from the workorder's created_by user.
 * If there is no workorder or created_by user, returns "-".
 */
export async function getRelatedBrokerName(tag: Tag): Promise<string> {
  try {
    // First check if we have a broker ID directly from the API response
    if (tag.broker) {
      const brokerResponse = await api.get(`/user/${tag.broker}`)
      const broker = brokerResponse.data
      return (
        `${broker.first_name || ''} ${broker.last_name || ''}`.trim() || '-'
      )
    }

    // If no direct broker ID, try to get it through the workorder
    const workorder = tag.workorder as unknown as { id: number } | null

    if (workorder?.id) {
      const workorderResponse = await api.get(`/workorder/${workorder.id}`)
      const workorderData = workorderResponse.data

      if (workorderData.created_by) {
        // workorderData.created_by is now a full User object, no need for additional API call
        return (
          `${workorderData.created_by.first_name || ''} ${workorderData.created_by.last_name || ''}`.trim() ||
          '-'
        )
      }
    }
  } catch (error) {
    console.error('Failed to fetch broker:', error)
  }
  return '-'
}

/**
 * Sets a site supervisor for a tag using the new API endpoint
 */
export const setSiteSupervisor = async (
  tagId: string,
  siteSupervisorId: number,
): Promise<Tag> => {
  try {
    // Use dedicated endpoint with required schema fields
    const primary = await api.patch(`/tag/${tagId}/site-supervisor/`, {
      supervisor_selection_mode: 'assigned',
      site_supervisor_id: siteSupervisorId,
    })
    return primary.data
  } catch (error) {
    const axiosErr = error as { response?: { status?: number; data?: unknown } }
    console.error(
      'Failed to set site supervisor:',
      axiosErr?.response?.data || error,
    )
    throw error
  }
}

/**
 * Notifies supervisor (assigned or custom) with confirmation details
 */
export const notifySupervisor = async (
  tagId: string,
  supervisorData: {
    supervisorSelectionMode: 'assigned' | 'custom'
    siteSupervisorId?: number
    customSupervisor?: {
      name: string
      email: string
      phone?: string
    }
    notificationType?: 'email' | 'link' | 'both'
  },
): Promise<{
  success: boolean
  message: string
  notification_id: string
  private_link?: string
  pdf_generated: boolean
}> => {
  try {
    const response = await api.post(`/tag/${tagId}/notify-supervisor/`, {
      supervisor_selection_mode: supervisorData.supervisorSelectionMode,
      site_supervisor_id: supervisorData.siteSupervisorId || null,
      custom_supervisor: supervisorData.customSupervisor || null,
      notification_type: 'link',
    })
    return response.data
  } catch (error) {
    console.error('Failed to notify supervisor:', error)
    throw error
  }
}
